/**
 * ч.1364: віджет «Фінанси місяця» — виручка (і план), прибуток, гроші на кінець дня.
 * Те саме зведення, що сторінка «Фінанси» (один джерело — Закон #6), за поточний місяць,
 * організація — з фільтра шапки (порожньо — усі, де є право на управлінські звіти).
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { finance, monthBounds } from '@/api/finance';
import { useOrgFilter } from '@/store/defaults';
import DashboardWidget from '../DashboardWidget';

const fmtMoney = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function Delta({ now, prev }: { now: number; prev: number }) {
  if (!prev) return null;
  const pct = Math.round(((now - prev) / Math.abs(prev)) * 100);
  const up = pct >= 0;
  return (
    <span style={{ fontSize: '0.78rem', color: up ? 'var(--badge-green-fg)' : 'var(--badge-red-fg)' }}
      title="проти минулого місяця">
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

export default function FinanceWidget() {
  const [orgId] = useOrgFilter();
  const today = new Date();
  const bounds = monthBounds(today.getFullYear(), today.getMonth() + 1);
  const q = useQuery({
    queryKey: ['dash-finance', orgId, bounds.period_start],
    queryFn: () => finance.summary({ ...bounds, organization_id: orgId || undefined }),
    staleTime: 120_000,
  });
  const d = q.data;
  const p = d?.profit;
  const plan = d?.plan;
  const pct = plan?.progress_pct ?? null;
  return (
    <DashboardWidget
      title="Фінанси місяця"
      loading={q.isLoading}
      error={q.isError ? 'Не вдалося завантажити зведення' : undefined}
      isEmpty={!q.isLoading && !!d && !p}
      emptyMessage="Немає організацій з правом на управлінські звіти"
      actions={<Link to="/finance" style={{ fontSize: '0.82rem' }} title="Сторінка «Фінанси»">докладно →</Link>}
    >
      {d && p && (
        <div style={{ display: 'grid', gap: '0.35rem', fontSize: '0.88rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ color: 'var(--muted)' }}>Виручка</span>
            <span style={{ fontSize: '1.3rem', fontWeight: 600, marginLeft: 'auto' }}>{fmtMoney(p.revenue.total)}</span>
            <Delta now={p.revenue.total} prev={p.revenue.prev} />
          </div>
          {plan?.available && plan.amount != null && (
            <div title={`План виручки на ${d.period.label}: ${fmtMoney(plan.amount)} грн`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--muted)' }}>
                <span>план {fmtMoney(plan.amount)}</span>
                <span>{pct != null ? `${Math.round(pct)}%` : ''}</span>
              </div>
              <div role="progressbar" aria-valuenow={Math.min(100, Math.round(pct ?? 0))} aria-valuemin={0}
                aria-valuemax={100} aria-label="Виконання плану виручки"
                style={{ height: 6, borderRadius: 3, background: 'var(--bg-soft-2)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, pct ?? 0)}%`, height: '100%',
                  background: (pct ?? 0) >= 100 ? 'var(--badge-green-fg)' : 'var(--primary)' }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ color: 'var(--muted)' }}>Прибуток</span>
            <span style={{ fontWeight: 600, marginLeft: 'auto',
              color: p.profit < 0 ? 'var(--badge-red-fg)' : undefined }}>{fmtMoney(p.profit)}</span>
            <Delta now={p.profit} prev={p.profit_prev} />
          </div>
          {d.cash && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ color: 'var(--muted)' }}>Гроші на рахунках і в касі</span>
              <span style={{ fontWeight: 600, marginLeft: 'auto' }}>{fmtMoney(d.cash.closing.total)}</span>
            </div>
          )}
          {d.debts && (
            <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: 'var(--muted)' }}>
              <span>винні нам {fmtMoney(d.debts.receivable.total)}</span>
              <span>винні ми {fmtMoney(d.debts.payable.total)}</span>
            </div>
          )}
        </div>
      )}
    </DashboardWidget>
  );
}
